import { useRef, useState, type KeyboardEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Play, Loader2 } from "lucide-react";
import { api, ApiError } from "../api";
import type { QueryResult } from "../types";
import { formatDuration } from "../lib/time";

interface Props {
  sandboxId: string;
}

const STARTER = "select table_name from information_schema.tables\nwhere table_schema = 'public'\norder by table_name;";

export function SqlRunner({ sandboxId }: Props) {
  const [sql, setSql] = useState(STARTER);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const mutation = useMutation<QueryResult, unknown, string>({
    mutationFn: (q: string) => api.runQuery(sandboxId, q),
    onSuccess: (_data, variables) => {
      setHistory((h) => (h[0] === variables ? h : [variables, ...h].slice(0, 50)));
      setHistoryIdx(null);
    },
  });

  const run = () => {
    const trimmed = sql.trim();
    if (!trimmed) return;
    mutation.mutate(trimmed);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter → run
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      run();
      return;
    }
    // History navigation only when caret is at first/last line boundary + no selection
    const ta = textareaRef.current;
    if (!ta) return;
    const atStart = ta.selectionStart === 0 && ta.selectionEnd === 0;
    const atEnd =
      ta.selectionStart === ta.value.length &&
      ta.selectionEnd === ta.value.length;

    if (e.key === "ArrowUp" && atStart && history.length > 0) {
      e.preventDefault();
      const next = historyIdx === null ? 0 : Math.min(historyIdx + 1, history.length - 1);
      setHistoryIdx(next);
      setSql(history[next]);
    } else if (e.key === "ArrowDown" && atEnd && historyIdx !== null) {
      e.preventDefault();
      if (historyIdx === 0) {
        setHistoryIdx(null);
        setSql("");
      } else {
        const next = historyIdx - 1;
        setHistoryIdx(next);
        setSql(history[next]);
      }
    }
  };

  const error = mutation.error;
  const data = mutation.data;

  return (
    <div className="space-y-4">
      <div className="bracket-frame !p-0">
        <span className="bracket-bl" />
        <span className="bracket-br" />
        <div className="flex items-center justify-between px-4 py-2 border-b border-ink-800 bg-ink-850">
          <span className="term-label">query</span>
          <span className="text-[10px] text-ink-500 uppercase tracking-widest">
            {history.length > 0 && `${history.length} in history — ↑/↓ to navigate · `}
            ⌘ + enter to run
          </span>
        </div>
        <textarea
          ref={textareaRef}
          value={sql}
          onChange={(e) => {
            setSql(e.target.value);
            setHistoryIdx(null);
          }}
          onKeyDown={onKeyDown}
          spellCheck={false}
          className="w-full min-h-[180px] bg-transparent px-4 py-3 font-mono text-sm text-ink-100 outline-none resize-y placeholder:text-ink-500"
          placeholder="-- write sql here"
        />
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-ink-800 bg-ink-850">
          <div className="text-[10px] text-ink-500 uppercase tracking-widest">
            {sql.length} chars
          </div>
          <button
            type="button"
            onClick={run}
            disabled={mutation.isPending || !sql.trim()}
            className="btn btn-primary"
          >
            {mutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                running…
              </>
            ) : (
              <>
                <Play size={12} />
                run
              </>
            )}
          </button>
        </div>
      </div>

      {error ? (
        <div className="border border-danger-900 bg-danger-900/20 p-4 flex gap-3">
          <AlertCircle className="text-danger-400 shrink-0 mt-0.5" size={16} />
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-danger-400">
              error
            </div>
            <pre className="text-xs text-ink-100 whitespace-pre-wrap break-words">
              {error instanceof ApiError ? error.message : String(error)}
            </pre>
          </div>
        </div>
      ) : null}

      {data && (
        <div className="animate-fade-in">
          <div className="flex items-baseline gap-4 mb-3">
            <div className="term-label">result</div>
            <div className="text-xs text-ink-400">
              <span className="text-phosphor-300 font-medium">
                {data.row_count}
              </span>{" "}
              row{data.row_count === 1 ? "" : "s"}
            </div>
            <div className="text-xs text-ink-400">
              <span className="text-phosphor-300">
                {formatDuration(data.duration_ms)}
              </span>
            </div>
          </div>

          {data.columns.length === 0 ? (
            <div className="border border-ink-700 bg-ink-900 px-4 py-6 text-xs text-ink-400 text-center">
              query executed — no rows returned
            </div>
          ) : (
            <div className="border border-ink-700 bg-ink-900 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-ink-700 bg-ink-850">
                    {data.columns.map((col, i) => (
                      <th
                        key={i}
                        className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest text-phosphor-400 font-medium"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, r) => (
                    <tr
                      key={r}
                      className="border-b border-ink-800 last:border-b-0 hover:bg-ink-850/60 animate-row-in"
                      style={{ animationDelay: `${Math.min(r * 12, 240)}ms` }}
                    >
                      {row.map((cell, c) => (
                        <td
                          key={c}
                          className="px-3 py-2 font-mono text-ink-200 align-top"
                        >
                          {renderCell(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function renderCell(cell: unknown): string {
  if (cell === null || cell === undefined)
    return "∅";
  if (typeof cell === "string") return cell;
  if (typeof cell === "number" || typeof cell === "boolean") return String(cell);
  try {
    return JSON.stringify(cell);
  } catch {
    return String(cell);
  }
}
